import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Github, Rocket, Loader2, ExternalLink, CheckCircle2, Lock } from "lucide-react";
import { api } from "../lib/api";

export default function DeployToVercelDialog({ open, onOpenChange, blueprint }) {
  const [repoName, setRepoName] = useState("");
  const [description, setDescription] = useState("");
  const [privateRepo, setPrivateRepo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [ghConnected, setGhConnected] = useState(null); // null=unknown, true/false

  useEffect(() => {
    if (!open) return;
    setResult(null);
    // Suggest repo name from title
    if (blueprint?.title) {
      const suggested = blueprint.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 50);
      setRepoName(suggested);
      setDescription(blueprint.tagline || `Generated by Durga Programming Adventures: ${blueprint.title}`);
    }
    // Check GH connection
    api.get("/github/me").then(({ data }) => setGhConnected(!!data?.login)).catch(() => setGhConnected(false));
  }, [open, blueprint]);

  if (!open) return null;

  const submit = async () => {
    if (!repoName.trim()) return toast.error("Give the repo a name.");
    setBusy(true);
    try {
      const { data } = await api.post("/website/push-github", {
        blueprint,
        repo_name: repoName.trim(),
        description,
        private: privateRepo,
      });
      setResult(data);
      toast.success("Pushed to GitHub — ready for Vercel!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Push failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const connectGh = async () => {
    try {
      const { data } = await api.get("/github/oauth/start");
      window.location.href = data.authorize_url;
    } catch {
      toast.error("Could not start GitHub OAuth.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm animate-fade-in"
      onClick={() => !busy && onOpenChange(false)}
      data-testid="deploy-vercel-dialog"
    >
      <div
        className="bg-cream rounded-3xl border-2 border-ink shadow-[10px_10px_0_0_#1A1B41] w-full max-w-lg max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 md:p-6 border-b-2 border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-ink text-cream flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink2 font-semibold">Deploy to Vercel</div>
            <div className="font-heading font-semibold text-lg truncate">{blueprint?.title || "Website"}</div>
          </div>
          <button onClick={() => !busy && onOpenChange(false)} className="btn-ghost text-sm p-2" data-testid="deploy-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 md:p-6 space-y-4">
          {ghConnected === false && !result && (
            <div className="rounded-2xl bg-marigold/10 border-2 border-marigold/40 p-4" data-testid="deploy-connect-gh">
              <div className="flex items-start gap-3">
                <Github className="w-5 h-5 text-marigold flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-ink mb-1">Connect GitHub first</div>
                  <p className="text-sm text-ink2 mb-3">
                    Vercel deploys from a GitHub repo. Link your GitHub once — we&apos;ll push the site there and hand you a Vercel one-click deploy link.
                  </p>
                  <button onClick={connectGh} className="btn-primary text-sm" data-testid="deploy-connect-gh-button">
                    <Github className="w-4 h-4" /> Connect GitHub
                  </button>
                </div>
              </div>
            </div>
          )}

          {ghConnected !== false && !result && (
            <>
              <p className="text-sm text-ink2">
                We&apos;ll create a fresh GitHub repo, push your generated Next.js site, and give you a
                one-click Vercel deploy link. Total time: about 30 seconds.
              </p>
            </>
          )}

          {!result && (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink2 font-semibold block mb-1">Repo name</label>
                <input
                  value={repoName}
                  onChange={(e) => setRepoName(e.target.value)}
                  placeholder="my-cool-site"
                  className="input-soft"
                  data-testid="deploy-repo-name"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-ink2 font-semibold block mb-1">
                  Description <span className="opacity-60">(optional)</span>
                </label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="A short line about the site"
                  className="input-soft"
                  data-testid="deploy-repo-description"
                />
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="deploy-private-toggle">
                <input
                  type="checkbox"
                  checked={privateRepo}
                  onChange={(e) => setPrivateRepo(e.target.checked)}
                  className="rounded border-ink"
                />
                <Lock className="w-3.5 h-3.5 text-ink2" />
                Make repo private
              </label>

              <button
                onClick={submit}
                disabled={busy || !repoName.trim() || ghConnected === false}
                className="btn-primary w-full justify-center disabled:opacity-40"
                data-testid="deploy-push-button"
                title={ghConnected === false ? "Connect GitHub first" : ""}
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Pushing to GitHub…</>
                ) : (
                  <><Github className="w-4 h-4" /> Push & Prepare Vercel</>
                )}
              </button>
            </>
          )}

          {result && (
            <div data-testid="deploy-result">
              <div className="flex items-center gap-2 mb-4 text-teal">
                <CheckCircle2 className="w-5 h-5" />
                <div className="font-heading font-semibold">Ready to deploy</div>
              </div>

              <div className="space-y-2 mb-4">
                <a
                  href={result.repo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-xl bg-paper border border-border hover:border-ink transition text-sm"
                  data-testid="deploy-repo-link"
                >
                  <Github className="w-4 h-4 text-ink2" />
                  <span className="flex-1 truncate">{result.repo_url}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-ink2" />
                </a>
              </div>

              <a
                href={result.vercel_deploy_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center"
                data-testid="deploy-vercel-button"
              >
                <Rocket className="w-4 h-4" /> Deploy to Vercel <ExternalLink className="w-3.5 h-3.5" />
              </a>

              <p className="text-xs text-ink2 mt-3 text-center">
                Opens Vercel&apos;s one-click import form with your new repo pre-filled. Sign in to Vercel (free),
                click Deploy, and your site is live in under a minute.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
